import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import SecurityDashboard from './SecurityDashboard';
import BackupHealth from './BackupHealth';
import AdminUserList from './AdminUserList';
import AdminGrowthGraphs from './AdminGrowthGraphs';
import './Admin.css';

const API = process.env.REACT_APP_API_URL;

function Admin() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState(null);
  const userManagementRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    const loadStats = async () => {
      try {
        const res = await axios.get(`${API}/admin/stats`, { headers });
        setStats(res.data);
      } catch (err) {
        console.error('Admin: loadStats failed:', err);
        setError(err.response?.status === 403 ? 'Access denied' : 'Failed to load stats');
      } finally {
        setLoading(false);
      }
    };
    loadStats();
  }, []);

  const handleSetActiveFilter = (filter) => {
    setActiveFilter(filter);
    if (filter !== null) {
      userManagementRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToUserManagement = () => {
    userManagementRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const diskPercent = stats ? parseInt(stats.disk.percent) : 0;
  const diskColor = diskPercent >= 90 ? '#c62828' : diskPercent >= 75 ? '#e65100' : '#2e7d32';

  if (loading) return <div className="admin-loading">Loading...</div>;
  if (error) return <div className="admin-error">{error}</div>;

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h2>Admin Dashboard</h2>
        <button className="admin-back-btn" onClick={() => navigate('/')}>Back</button>
      </div>

      {diskPercent >= 85 && (
        <div className="admin-disk-warning">
          Disk at {stats.disk.percent} — clean up Docker images: docker image prune -a --force
        </div>
      )}

      <div className="admin-stats-grid">
        {[
          { label: 'Total Users',     value: stats.users.total,        color: '#2e7d32', bg: '#e8f5e9', onClick: () => handleSetActiveFilter({ type: 'all',       value: null, label: 'All users' }) },
          { label: 'New (7 days)',    value: stats.users.lastWeek,     color: '#1565c0', bg: '#e3f2fd' },
          { label: 'New (30 days)',   value: stats.users.lastMonth,    color: '#1565c0', bg: '#e3f2fd' },
          { label: 'Active (7 days)',  value: stats.users.activeWeek,  color: '#2e7d32', bg: '#e8f5e9', onClick: () => handleSetActiveFilter({ type: 'active7',   value: null, label: 'Active last 7 days' }) },
          { label: 'Active (30 days)', value: stats.users.activeMonth, color: '#2e7d32', bg: '#e8f5e9', onClick: () => handleSetActiveFilter({ type: 'active30',  value: null, label: 'Active last 30 days' }) },
          { label: 'Properties',     value: stats.content.properties, color: '#6a1b9a', bg: '#f3e5f5' },
          { label: 'Plants',         value: stats.content.plants,     color: '#2e7d32', bg: '#e8f5e9', onClick: () => handleSetActiveFilter({ type: 'hasPlants', value: null, label: 'Users with plants' }) },
          { label: 'Photos',         value: stats.content.photos,     color: '#e65100', bg: '#fff3e0', onClick: () => handleSetActiveFilter({ type: 'hasPhotos', value: null, label: 'Users with photos' }) },
          { label: 'Tasks',          value: stats.content.tasks,      color: '#f57c00', bg: '#fff3e0', onClick: () => handleSetActiveFilter({ type: 'hasTasks',  value: null, label: 'Users with tasks' }) },
        ].map(({ label, value, color, bg, onClick }) => (
          <div
            key={label}
            className={`admin-stat-card${onClick ? ' admin-stat-card--clickable' : ''}`}
            style={{ backgroundColor: bg }}
            onClick={onClick}
          >
            <div className="admin-stat-value" style={{ color }}>{value}</div>
            <div className="admin-stat-label">{label}</div>
          </div>
        ))}
      </div>

      <div className="admin-card admin-card--clickable" onClick={scrollToUserManagement}>
        <h3>Server Health</h3>
        <div className="admin-grid-2">
          <div>
            <div className="admin-disk-label">Disk Space</div>
            <div className="admin-db-value" style={{ color: diskColor }}>
              {stats.disk.free} free of {stats.disk.total} ({stats.disk.percent})
            </div>
            <div className="admin-disk-bar-bg">
              <div style={{ width: stats.disk.percent, backgroundColor: diskColor, borderRadius: '4px', height: '8px', transition: 'width 0.3s' }} />
            </div>
          </div>
          <div>
            <div className="admin-disk-label">Database Size</div>
            <div className="admin-db-value">{stats.database.size}</div>
          </div>
        </div>
      </div>

      <div className="admin-card">
        <h3>Recent Signups (30 days)</h3>
        {stats.recentSignups.length === 0 ? (
          <div className="admin-empty-text">No signups in the last 30 days</div>
        ) : (
          <div className="admin-list">
            {stats.recentSignups.map((row, i) => {
              const dateLabel = new Date(row.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
              return (
                <div
                  key={i}
                  className="admin-row admin-row--clickable"
                  onClick={() => handleSetActiveFilter({ type: 'signupDate', value: row.date.substring(0, 10), label: `Signed up ${dateLabel}` })}
                >
                  <span className="admin-row-left">{dateLabel}</span>
                  <span className="admin-row-right">{row.signups} signup{row.signups > 1 ? 's' : ''}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="admin-card">
        <h3>Properties by USDA Zone</h3>
        {stats.zones.length === 0 ? (
          <div className="admin-empty-text">No zone data yet</div>
        ) : (
          <div className="admin-zones-list">
            {stats.zones.map((z, i) => (
              <div
                key={i}
                className="admin-zone-card admin-zone-card--clickable"
                onClick={() => handleSetActiveFilter({ type: 'zone', value: z.usda_zone, label: `Zone ${z.usda_zone} users` })}
              >
                <div className="admin-zone-value">Zone {z.usda_zone}</div>
                <div className="admin-zone-count">{z.count} {z.count === '1' ? 'property' : 'properties'}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="admin-card">
        <h3>Most Active Users</h3>
        <div className="admin-list">
          {stats.topUsers.map((u, i) => (
            <div key={i} className="admin-user-row">
              <span className="admin-row-left">{u.email}</span>
              <span className="admin-row-right">{u.plant_count} plants</span>
            </div>
          ))}
        </div>
      </div>

      <div className="admin-card">
        <h3>Growth &amp; Analytics</h3>
        <AdminGrowthGraphs setActiveFilter={handleSetActiveFilter} />
      </div>
      <div className="admin-card" ref={userManagementRef}>
        <h3>User Management</h3>
        <AdminUserList activeFilter={activeFilter} setActiveFilter={setActiveFilter} />
      </div>
      <SecurityDashboard />
      <BackupHealth />
    </div>
  );
}

export default Admin;
